import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Pagination,
  Typography,
  Button,
  Grid,
  Tabs,
  Tab,
  Chip,
  LinearProgress,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useTheme } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useAuth } from '../../context/AuthContext';
import useFetchObjects from '../../api/useFetchObjects';
import { Download } from '@mui/icons-material';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { exportComprehensivePurchaseReportToPDF } from '../../utils/exportToPDF';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const normalizeList = (data) => (Array.isArray(data) ? data : data?.results || []);
const ALL_SUPPLIERS_OPTION = { id: '', name: 'All Suppliers' };

const getOptionTitle = (options, value) => {
  const selected = options.find((option) => String(option.id) === String(value));
  return selected?.title || selected?.name || value || '-';
};

const parseStoredAmount = (value) => {
  if (!value && value !== 0) return 0;
  if (typeof value === 'number') return value;
  const cleaned = String(value).replace(/,/g, '').replace(/[^0-9.-]/g, '');
  return Number(cleaned) || 0;
};

const readStoredSupplierReportRows = (suppliers = []) => {
  if (typeof window === 'undefined') return [];

  const rows = [];
  const supplierMap = new Map(suppliers.map((supplier) => [String(supplier.id), supplier]));

  Object.keys(window.localStorage)
    .filter((key) => key.startsWith('agent-items-'))
    .forEach((key) => {
      const supplierId = key.replace('agent-items-', '');
      const supplier = supplierMap.get(String(supplierId));
      const supplierType = String(supplier?.agent_type || '').toLowerCase();
      if (supplierType !== 'supplier') return;

      let parsed = [];
      try {
        const raw = window.localStorage.getItem(key);
        const maybeData = JSON.parse(raw);
        parsed = Array.isArray(maybeData) ? maybeData : [];
      } catch {
        parsed = [];
      }

      parsed.forEach((item, index) => {
        const total = parseStoredAmount(item?.total);
        const paid = parseStoredAmount(item?.paid);
        rows.push({
          id: `${supplierId}-${item?.billNo || index}`,
          supplierId,
          billNo: item?.billNo || '-',
          date: item?.date || '-',
          description: item?.description || '-',
          unit: item?.unit || '-',
          qty: item?.qty || 0,
          currency: item?.currency || '-',
          total,
          paid,
          remaining: total - paid,
        });
      });
    });

  return rows.sort((a, b) => {
    const aTime = new Date(a.date).getTime();
    const bTime = new Date(b.date).getTime();
    if (Number.isNaN(aTime) && Number.isNaN(bTime)) return 0;
    if (Number.isNaN(aTime)) return 1;
    if (Number.isNaN(bTime)) return -1;
    return bTime - aTime;
  });
};

export default function ComprehensivePurchaseReport({ selectedBranch = "" }) {
  const theme = useTheme();
  const isDarkMode = theme?.palette?.mode === 'dark';
  const auth = useAuth();
  const token = auth?.user?.token;
  
  const [tabValue, setTabValue] = useState(0);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [viewMode, setViewMode] = useState('simple');
  const [supplierReportPage, setSupplierReportPage] = useState(0);
  const [supplierReportRowsPerPage, setSupplierReportRowsPerPage] = useState(10);

  const queryString = useMemo(() => {
    const params = ["page_size=all"]; // Always fetch all records for reports
    if (startDate) params.push(`start_date=${startDate.format('YYYY-MM-DD')}`);
    if (endDate) params.push(`end_date=${endDate.format('YYYY-MM-DD')}`);
    if (selectedBranch) params.push(`shop=${selectedBranch}`);
    if (selectedSupplier) params.push(`supplier=${selectedSupplier}`);
    return `?${params.join('&')}`;
  }, [startDate, endDate, selectedBranch, selectedSupplier]);

  const { data: purchasesData, isLoading: purchasesLoading } = useFetchObjects(
    ['purchases', queryString], `shop/purchases/${queryString}`, token
  );
  const { data: paymentsData, isLoading: paymentsLoading } = useFetchObjects(
    ['purchase-payments', queryString], `shop/purchase-payments/${queryString}`, token
  );
  const { data: returnsData, isLoading: returnsLoading } = useFetchObjects(
    ['purchase-returns', queryString], `shop/purchase-returns/${queryString}`, token
  );
  const { data: agentsData } = useFetchObjects(
    ['supplier-report-agents'], `shop/customers/?page_size=all`, token
  );
  const { data: suppliersData } = useFetchObjects(
    ['suppliers'], `shop/suppliers/`, token
  );
  const { data: unitsData } = useFetchObjects(
    ['supplier-report-units'], `shop/units/?page_size=all`, token
  );
  const { data: currenciesData } = useFetchObjects(
    ['supplier-report-currencies'], `shop/currencies/?page_size=all`, token
  );

  const purchases = purchasesData?.results || purchasesData || [];
  const payments = paymentsData?.results || paymentsData || [];
  const returns = returnsData?.results || returnsData || [];
  const agents = normalizeList(agentsData);
  const suppliers = normalizeList(suppliersData);
  const units = normalizeList(unitsData);
  const currencies = normalizeList(currenciesData);
  const supplierOptions = useMemo(
    () => [
      ALL_SUPPLIERS_OPTION,
      ...agents.filter((agent) => String(agent?.agent_type || '').toLowerCase() === 'supplier'),
    ],
    [agents]
  );
  const selectedSupplierObj = useMemo(() => {
    if (!selectedSupplier) return ALL_SUPPLIERS_OPTION;
    return (
      supplierOptions.find((supplier) => String(supplier.id) === String(selectedSupplier)) ||
      ALL_SUPPLIERS_OPTION
    );
  }, [selectedSupplier, supplierOptions]);
  
  const isLoading = purchasesLoading || paymentsLoading || returnsLoading;

  const parseAmount = (value) => {
    if (!value && value !== 0) return 0;
    if (typeof value === 'number') return value;
    if (typeof value === 'object' && value !== null) {
      const currencies = Object.keys(value);
      if (currencies.length > 0) {
        return parseFloat(value[currencies[0]]) || 0;
      }
    }
    const numStr = String(value).replace(/[^0-9.-]/g, '');
    return parseFloat(numStr) || 0;
  };

  const summary = {
    total_purchases: {
      count: purchases.length,
      amount: purchases.reduce((sum, p) => sum + parseAmount(p.total_price), 0)
    },
    total_payments: {
      count: payments.length,
      amount: payments.reduce((sum, p) => sum + parseAmount(p.amount), 0)
    },
    total_returns: {
      count: returns.length,
      amount: returns.reduce((sum, r) => sum + parseAmount(r.total_refund_amount || (r.return_items?.[0]?.refund_amount)), 0)
    },
    total_paid: purchases.reduce((sum, p) => sum + parseAmount(p.total_paid), 0),
    total_remaining: purchases.reduce((sum, p) => sum + parseAmount(p.total_remained_amount), 0),
    outstanding_amount: purchases.reduce((sum, p) => sum + parseAmount(p.total_remained_amount), 0),
    payment_status: {
      paid: purchases.filter(p => parseAmount(p.total_remained_amount) === 0).length,
      partial: purchases.filter(p => parseAmount(p.total_remained_amount) > 0 && parseAmount(p.total_paid) > 0).length,
      unpaid: purchases.filter(p => parseAmount(p.total_paid) === 0).length
    }
  };

  const supplierReportRows = useMemo(
    () => readStoredSupplierReportRows(agents),
    [agents]
  );

  const filteredSupplierReportRows = useMemo(() => {
    const start = startDate ? startDate.startOf('day') : null;
    const end = endDate ? endDate.endOf('day') : null;

    return supplierReportRows.filter((row) => {
      if (selectedSupplier && String(row.supplierId) !== String(selectedSupplier)) {
        return false;
      }

      if (!start && !end) return true;

      const rowDate = new Date(row.date);
      if (Number.isNaN(rowDate.getTime())) return false;
      if (start && rowDate < start.toDate()) return false;
      if (end && rowDate > end.toDate()) return false;
      return true;
    });
  }, [endDate, selectedSupplier, supplierReportRows, startDate]);

  const pagedSupplierReportRows = useMemo(() => {
    if (supplierReportRowsPerPage === -1) return filteredSupplierReportRows;
    const start = supplierReportPage * supplierReportRowsPerPage;
    return filteredSupplierReportRows.slice(start, start + supplierReportRowsPerPage);
  }, [filteredSupplierReportRows, supplierReportPage, supplierReportRowsPerPage]);

  const supplierReportSummary = useMemo(() => {
    const totalQty = filteredSupplierReportRows.reduce(
      (sum, row) => sum + parseAmount(row.qty),
      0
    );
    const totalAmount = filteredSupplierReportRows.reduce(
      (sum, row) => sum + parseAmount(row.total),
      0
    );
    const totalPaid = filteredSupplierReportRows.reduce(
      (sum, row) => sum + parseAmount(row.paid),
      0
    );

    return {
      records: filteredSupplierReportRows.length,
      totalQty,
      totalAmount,
      totalPaid,
      remaining: totalAmount - totalPaid,
    };
  }, [filteredSupplierReportRows]);

  useEffect(() => {
    setSupplierReportPage(0);
  }, [endDate, selectedSupplier, startDate]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSupplierChange = (event, newValue) => {
    setSelectedSupplier(newValue?.id ? String(newValue.id) : '');
  };

  const exportToPDF = () => {
    const exportData = {
      purchases,
      payments,
      returns,
      summary,
      suppliers
    };
    exportComprehensivePurchaseReportToPDF(exportData);
  };

  const clearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedSupplier('');
    setSupplierReportPage(0);
  };

  const exportSupplierReport = () => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const timestamp = new Date().toLocaleString();
    const supplierName = selectedSupplierObj?.name || 'All Suppliers';
    const startLabel = startDate ? startDate.format('YYYY-MM-DD') : 'All';
    const endLabel = endDate ? endDate.format('YYYY-MM-DD') : 'All';

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Supplier Report', pageWidth / 2, 12, { align: 'center' });
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Supplier: ${supplierName}`, 14, 22);
    doc.text(`Start Date: ${startLabel}`, 14, 28);
    doc.text(`End Date: ${endLabel}`, 14, 34);
    doc.text(`Generated: ${timestamp}`, pageWidth - 14, 22, { align: 'right' });

    autoTable(doc, {
      startY: 40,
      head: [['Bill No.', 'Date', 'Description', 'Unit', 'Qty', 'Currency', 'Total']],
      body: filteredSupplierReportRows.map((row) => [
        row.billNo || '-',
        row.date || '-',
        row.description || '-',
        getOptionTitle(units, row.unit),
        Number(row.qty || 0).toLocaleString(),
        getOptionTitle(currencies, row.currency),
        Number(row.total || 0).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
      ]),
      styles: {
        fontSize: 8,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [53, 93, 49],
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { top: 14, left: 6, right: 6 },
    });

    const totalQty = filteredSupplierReportRows.reduce(
      (sum, row) => sum + parseAmount(row.qty),
      0
    );
    const totalAmount = filteredSupplierReportRows.reduce(
      (sum, row) => sum + parseAmount(row.total),
      0
    );
    const totalPaid = filteredSupplierReportRows.reduce(
      (sum, row) => sum + parseAmount(row.paid),
      0
    );
    const totalRemaining = totalAmount - totalPaid;

    const finalY = doc.lastAutoTable?.finalY || 50;
    autoTable(doc, {
      startY: finalY + 8,
      head: [['Total Qty', 'Total Amount', 'Total Paid', 'Remaining']],
      body: [[
        totalQty.toLocaleString(),
        totalAmount.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        totalPaid.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        totalRemaining.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
      ]],
      styles: {
        fontSize: 8,
        cellPadding: 2,
        halign: 'center',
      },
      headStyles: {
        fillColor: [53, 93, 49],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      bodyStyles: {
        fontStyle: 'bold',
        textColor: [53, 93, 49],
      },
      margin: { left: 6, right: 6 },
    });

    doc.save(`Supplier_Report_${new Date().toISOString().slice(0, 19).replace(/[:]/g, '-')}.pdf`);
  };

  const exportSupplierReportToExcel = () => {
    const workbook = XLSX.utils.book_new();
    const supplierName = selectedSupplierObj?.name || 'All Suppliers';
    const dataRows = filteredSupplierReportRows.map((row) => ({
      'Bill No.': row.billNo || '-',
      Date: row.date || '-',
      Description: row.description || '-',
      Unit: getOptionTitle(units, row.unit),
      Qty: Number(row.qty || 0),
      Currency: getOptionTitle(currencies, row.currency),
      Total: Number(row.total || 0),
    }));

    const summaryRows = [
      { Metric: 'Supplier', Value: supplierName },
      { Metric: 'Start Date', Value: startDate ? startDate.format('YYYY-MM-DD') : 'All' },
      { Metric: 'End Date', Value: endDate ? endDate.format('YYYY-MM-DD') : 'All' },
      { Metric: 'Records', Value: filteredSupplierReportRows.length },
      {
        Metric: 'Total Qty',
        Value: filteredSupplierReportRows.reduce((sum, row) => sum + parseAmount(row.qty), 0),
      },
      {
        Metric: 'Total Amount',
        Value: filteredSupplierReportRows.reduce((sum, row) => sum + parseAmount(row.total), 0),
      },
      {
        Metric: 'Remaining',
        Value: filteredSupplierReportRows.reduce((sum, row) => sum + parseAmount(row.total), 0),
      },
    ];

    const summarySheet = XLSX.utils.json_to_sheet(summaryRows);
    summarySheet['!cols'] = [{ wch: 20 }, { wch: 24 }];
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

    const dataSheet = XLSX.utils.json_to_sheet(dataRows);
    dataSheet['!cols'] = [
      { wch: 12 },
      { wch: 14 },
      { wch: 40 },
      { wch: 12 },
      { wch: 12 },
      { wch: 14 },
      { wch: 14 },
    ];
    XLSX.utils.book_append_sheet(workbook, dataSheet, 'Supplier Report');

    XLSX.writeFile(
      workbook,
      `Supplier_Report_${new Date().toISOString().slice(0, 19).replace(/[:]/g, '-')}.xlsx`
    );
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          p: 3,
          minHeight: '100%',
          backgroundColor: isDarkMode ? '#121212' : 'transparent',
          color: isDarkMode ? '#f3f4f6' : 'inherit',
        }}
      >
        <LinearProgress />
        <Typography sx={{ mt: 2, color: isDarkMode ? '#d1d5db' : 'inherit' }}>
          Loading purchase data...
        </Typography>
      </Box>
    );
  }

  const surfaceBg = isDarkMode ? '#1a1a1a' : '#fff';
  const surfaceBorder = isDarkMode ? '#2f2f2f' : '#e5e7eb';
  const mutedText = isDarkMode ? '#a9b4c4' : 'text.secondary';
  const titleText = isDarkMode ? '#f9fafb' : '#132238';
  const tableRowBg = isDarkMode ? '#1b1b1b' : '#fff';
  const tableRowHover = isDarkMode ? '#232323' : '#f8fbff';

  if (viewMode === 'simple') {
    return (
      <Box
        sx={{
          width: '100%',
          minHeight: '100%',
          backgroundColor: isDarkMode ? '#121212' : 'transparent',
          color: isDarkMode ? '#f3f4f6' : '#132238',
        }}
      >
        <Paper
          sx={{
            p: { xs: 2, md: 3 },
            backgroundColor: surfaceBg,
            color: isDarkMode ? '#f3f4f6' : '#132238',
            border: `1px solid ${surfaceBorder}`,
            borderRadius: 2,
            boxShadow: isDarkMode ? '0 10px 24px rgba(0,0,0,0.3)' : undefined,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', md: 'center' },
              gap: 2,
              mb: 2,
              flexDirection: { xs: 'column', md: 'row' },
            }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#355d31' }}>
                Supplier Report
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Purchase records appear here automatically.
              </Typography>
            </Box>
          </Box>

          <Paper
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: surfaceBg,
              color: isDarkMode ? '#f3f4f6' : '#132238',
              border: `1px solid ${surfaceBorder}`,
              boxShadow: isDarkMode ? '0 10px 24px rgba(0,0,0,0.3)' : undefined,
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={2}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={setStartDate}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={setEndDate}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    fullWidth
                    options={supplierOptions}
                    value={selectedSupplierObj}
                    onChange={handleSupplierChange}
                    getOptionLabel={(option) => option?.name || ''}
                    isOptionEqualToValue={(option, value) =>
                      String(option?.id ?? '') === String(value?.id ?? '')
                    }
                    clearOnBlur={false}
                    renderInput={(params) => <TextField {...params} label="Supplier" />}
                  />
                </Grid>
                <Grid item xs={12} md={1}>
                  <Button variant="outlined" onClick={clearFilters} fullWidth sx={{ height: 56 }}>
                    Clear
                  </Button>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    variant="contained"
                    onClick={exportSupplierReport}
                    startIcon={<Download />}
                    fullWidth
                    sx={{ height: 56 }}
                  >
                    Export PDF
                  </Button>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    variant="outlined"
                    onClick={exportSupplierReportToExcel}
                    startIcon={<Download />}
                    fullWidth
                    sx={{ height: 56 }}
                  >
                    Export Excel
                  </Button>
                </Grid>
              </Grid>
            </LocalizationProvider>
          </Paper>

          <Box
            sx={{
              mb: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700, color: titleText }}>
              Rows per page
            </Typography>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <Select
                value={supplierReportRowsPerPage}
                onChange={(event) => {
                  const nextValue = Number(event.target.value);
                  setSupplierReportRowsPerPage(nextValue);
                  setSupplierReportPage(0);
                }}
                sx={{
                  height: 40,
                  backgroundColor: surfaceBg,
                  color: isDarkMode ? '#f3f4f6' : '#132238',
                }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={30}>30</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={-1}>All</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Typography
            variant="h4"
            sx={{
              textAlign: 'center',
              mb: 2,
              color: titleText,
              fontWeight: 400,
            }}
          >
            Supplier Report Data Table
          </Typography>

          <TableContainer
            sx={{
              border: `1px solid ${surfaceBorder}`,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['Bill No.', 'Date', 'Description', 'Unit', 'Qty', 'Currency', 'Total'].map(
                    (label) => (
                      <TableCell
                        key={label}
                        sx={{
                          backgroundColor: '#355d31',
                          color: '#fff',
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {label}
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedSupplierReportRows.length ? (
                  pagedSupplierReportRows.map((row, index) => (
                    <TableRow
                      key={`${row.id}-${supplierReportPage}-${index}`}
                      hover
                      sx={{
                        '&:nth-of-type(even)': {
                          backgroundColor: isDarkMode ? '#171717' : '#fafcfe',
                        },
                        '& td': {
                          borderColor: surfaceBorder,
                        },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 700 }}>{row.billNo}</TableCell>
                      <TableCell>{row.date}</TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 460,
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          lineHeight: 1.5,
                        }}
                      >
                        {row.description}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getOptionTitle(units, row.unit)}
                          size="small"
                          sx={{
                            backgroundColor: isDarkMode ? '#243f23' : '#edf6ec',
                            color: isDarkMode ? '#d9f0d9' : '#355d31',
                            fontWeight: 700,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {Number(row.qty || 0).toLocaleString()}
                      </TableCell>
                      <TableCell>{getOptionTitle(currencies, row.currency)}</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1f7a4c' }}>
                        {Number(row.total || 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                      No supplier report records found.
                    </TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell
                    colSpan={7}
                    sx={{
                      borderTop: `1px solid ${surfaceBorder}`,
                      py: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        justifyContent: "flex-end",
                        alignItems: "center",
                      }}
                    >
                      {[
                        ["Qty", supplierReportSummary.totalQty, "#2f7d32", "#e8f5e9"],
                        ["Total", supplierReportSummary.totalAmount, "#355d31", "#edf6ec"],
                        ["Paid", supplierReportSummary.totalPaid, "#2f7d32", "#eef7ee"],
                        ["Remaining", supplierReportSummary.remaining, "#d32f2f", "#fdecec"],
                      ].map(([label, value, borderColor, bgColor]) => (
                        <Box
                          key={label}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 1,
                            px: 1.6,
                            py: 0.8,
                            borderRadius: 999,
                            border: `1px solid ${borderColor}`,
                            backgroundColor: bgColor,
                            whiteSpace: "nowrap",
                            fontWeight: 700,
                            fontSize: 13,
                          }}
                        >
                          <Box component="span" sx={{ color: "text.secondary" }}>
                            {label}
                          </Box>
                          <Box component="span" sx={{ color: borderColor }}>
                            {Number(value || 0).toLocaleString(undefined, {
                              maximumFractionDigits: 2,
                            })}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={
                supplierReportRowsPerPage === -1
                  ? 1
                  : Math.max(
                      1,
                      Math.ceil(
                        filteredSupplierReportRows.length / supplierReportRowsPerPage
                      )
                    )
              }
              page={supplierReportRowsPerPage === -1 ? 1 : supplierReportPage + 1}
              onChange={(_, nextPage) => setSupplierReportPage(nextPage - 1)}
              shape="rounded"
              color="success"
            />
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100%',
        backgroundColor: isDarkMode ? '#121212' : 'transparent',
        color: isDarkMode ? '#f3f4f6' : '#132238',
      }}
    >
      <Typography variant="h4" sx={{ mb: 3, color: titleText }}>
        Comprehensive Purchase Report
      </Typography>

      {/* View Mode Toggle */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
        <Button
          variant={viewMode === 'simple' ? 'contained' : 'outlined'}
          onClick={() => setViewMode('simple')}
          sx={{ mr: 1 }}
        >
          📊 Simple View
        </Button>
        <Button
          variant={viewMode === 'detailed' ? 'contained' : 'outlined'}
          onClick={() => setViewMode('detailed')}
        >
          📋 Detailed View
        </Button>
      </Box>

      {viewMode === 'simple' ? (
        /* Simple View */
        <Box>
          <Paper
            sx={{
              p: 3,
              mb: 3,
              textAlign: 'center',
              backgroundColor: surfaceBg,
              color: isDarkMode ? '#f3f4f6' : '#132238',
              border: `1px solid ${surfaceBorder}`,
              boxShadow: isDarkMode ? '0 10px 24px rgba(0,0,0,0.3)' : undefined,
            }}
          >
            <Typography variant="h5" sx={{ mb: 2, color: '#355d31' }}>
              📈 Quick Overview
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={2.4}>
                <Box sx={{ p: 2, bgcolor: '#3e6f38', borderRadius: 2, textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {summary.total_purchases.count}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'white' }}>
                    Total Purchases
                  </Typography>
                  <Typography variant="h6" sx={{ color: 'white' }}>
                    {summary.total_purchases.amount.toFixed(2)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Box sx={{ p: 2, bgcolor: '#5fba5d', borderRadius: 2, textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {summary.total_paid.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'white' }}>
                    Total Paid
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'white' }}>
                    {summary.total_payments.count} payments
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Box sx={{ p: 2, bgcolor: '#ffac2f', borderRadius: 2, textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {summary.outstanding_amount.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'white' }}>
                    Outstanding
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'white' }}>
                    Remaining to Pay
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Box sx={{ p: 2, bgcolor: '#e0005a', borderRadius: 2, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: 'white', mb: 1 }}>
                    Payment Status
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'white', display: 'block' }}>
                    ✅ Paid: {summary.payment_status.paid}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'white', display: 'block' }}>
                    ⏳ Partial: {summary.payment_status.partial}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'white', display: 'block' }}>
                    ❌ Unpaid: {summary.payment_status.unpaid}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          <Paper
            sx={{
              p: 2,
              backgroundColor: surfaceBg,
              color: isDarkMode ? '#f3f4f6' : '#132238',
              border: `1px solid ${surfaceBorder}`,
              boxShadow: isDarkMode ? '0 10px 24px rgba(0,0,0,0.3)' : undefined,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, color: titleText }}>Recent Purchases</Typography>
            {purchases.slice(0, 5).map((purchase) => (
              <Box key={purchase.id} sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                p: 2, 
                mb: 1, 
                bgcolor: isDarkMode ? '#232323' : 'grey.50', 
                borderRadius: 1,
                border: `1px solid ${isDarkMode ? '#2f2f2f' : '#e5e7eb'}`
              }}>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    #{purchase.id} - {purchase.supplier_details?.name || 'Unknown'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: mutedText }}>
                    {purchase.purchase_date}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h6" sx={{ color: '#355d31' }}>
                    {parseAmount(purchase.total_price).toFixed(2)}
                  </Typography>
                  <Chip 
                    label={parseAmount(purchase.total_remained_amount) === 0 ? '✅ Paid' : '⏳ Pending'}
                    size="small"
                    color={parseAmount(purchase.total_remained_amount) === 0 ? 'success' : 'warning'}
                  />
                </Box>
              </Box>
            ))}
          </Paper>
        </Box>
      ) : (
        /* Detailed View */
        <Box>
          {/* Comprehensive Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={2.4}>
              <Card sx={{ bgcolor: isDarkMode ? '#284d24' : 'primary.light' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ color: isDarkMode ? '#d1e7d1' : 'primary.main' }}>Total Purchases</Typography>
                  <Typography variant="h4" sx={{ color: isDarkMode ? '#ffffff' : 'primary.main' }}>{summary.total_purchases.count}</Typography>
                  <Typography variant="body2" sx={{ color: isDarkMode ? '#d1d5db' : 'text.secondary' }}>Amount: {summary.total_purchases.amount.toFixed(2)}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2.4}>
              <Card sx={{ bgcolor: isDarkMode ? '#245c2d' : 'success.light' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ color: isDarkMode ? '#d9f5dd' : 'success.main' }}>Total Paid</Typography>
                  <Typography variant="h4" sx={{ color: isDarkMode ? '#ffffff' : 'success.main' }}>{summary.total_paid.toFixed(2)}</Typography>
                  <Typography variant="body2" sx={{ color: isDarkMode ? '#d1d5db' : 'text.secondary' }}>{summary.total_payments.count} payments</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2.4}>
              <Card sx={{ bgcolor: 'warning.light' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="warning.main">Outstanding</Typography>
                  <Typography variant="h4" color="warning.main">{summary.outstanding_amount.toFixed(2)}</Typography>
                  <Typography variant="body2">Remaining to Pay</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2.4}>
              <Card sx={{ bgcolor: 'info.light' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="info.main">Total Returns</Typography>
                  <Typography variant="h4" color="info.main">{summary.total_returns.count}</Typography>
                  <Typography variant="body2">Amount: {summary.total_returns.amount.toFixed(2)}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2.4}>
              <Card sx={{ bgcolor: isDarkMode ? '#5d2351' : 'secondary.light' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="secondary.main">Payment Status</Typography>
                  <Typography variant="body2" color="success.main">✅ Paid: {summary.payment_status.paid}</Typography>
                  <Typography variant="body2" color="warning.main">⏳ Partial: {summary.payment_status.partial}</Typography>
                  <Typography variant="body2" color="error.main">❌ Unpaid: {summary.payment_status.unpaid}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Financial Summary */}
          <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
            <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>💰 Complete Financial Overview</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, border: '2px solid', borderColor: 'primary.main', borderRadius: 2 }}>
                  <Typography variant="h6" color="primary" sx={{ mb: 1 }}>Purchase Summary</Typography>
                  <Typography variant="body1">Total Purchase Amount: <strong>{summary.total_purchases.amount.toFixed(2)}</strong></Typography>
                  <Typography variant="body1">Total Paid Amount: <strong>{summary.total_paid.toFixed(2)}</strong></Typography>
                  <Typography variant="body1">Outstanding Amount: <strong>{summary.outstanding_amount.toFixed(2)}</strong></Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Payment Progress: {summary.total_purchases.amount > 0 ? ((summary.total_paid / summary.total_purchases.amount) * 100).toFixed(1) : 0}%
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, border: '2px solid', borderColor: 'info.main', borderRadius: 2 }}>
                  <Typography variant="h6" color="info.main" sx={{ mb: 1 }}>Additional Information</Typography>
                  <Typography variant="body1">Total Returns: <strong>{summary.total_returns.amount.toFixed(2)}</strong></Typography>
                  <Typography variant="body1">Net Purchase Value: <strong>{(summary.total_purchases.amount - summary.total_returns.amount).toFixed(2)}</strong></Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Note: Returns don't affect outstanding amount as it represents final remaining balance
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Filters */}
          <Box sx={{ mb: 3 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={setStartDate}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={setEndDate}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Supplier</InputLabel>
                    <Select
                      value={selectedSupplier}
                      onChange={(e) => setSelectedSupplier(e.target.value)}
                      label="Supplier"
                    >
                      <MenuItem value="">All Suppliers</MenuItem>
                      {suppliers.map((supplier) => (
                        <MenuItem key={supplier.id} value={supplier.id}>
                          {supplier.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={1}>
                  <Button variant="outlined" onClick={clearFilters} fullWidth>
                    Clear
                  </Button>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button 
                    variant="contained" 
                    onClick={exportToPDF} 
                    startIcon={<Download />}
                    fullWidth
                  >
                    Export PDF
                  </Button>
                </Grid>
              </Grid>
            </LocalizationProvider>
          </Box>

          {/* Tabs */}
          <Paper sx={{ width: '100%' }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label={`Purchases (${purchases.length})`} />
              <Tab label={`Payments (${payments.length})`} />
              <Tab label={`Returns (${returns.length})`} />
            </Tabs>

            {/* Purchase Tab */}
            <TabPanel value={tabValue} index={0}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Supplier</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Total Amount</TableCell>
                      <TableCell>Paid</TableCell>
                      <TableCell>Remaining</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {purchases.map((purchase) => (
                      <TableRow key={purchase.id}>
                        <TableCell>#{purchase.id}</TableCell>
                        <TableCell>{purchase.supplier_details?.name || 'N/A'}</TableCell>
                        <TableCell>{purchase.purchase_date}</TableCell>
                        <TableCell>{parseAmount(purchase.total_price).toFixed(2)}</TableCell>
                        <TableCell>{parseAmount(purchase.total_paid).toFixed(2)}</TableCell>
                        <TableCell>{parseAmount(purchase.total_remained_amount).toFixed(2)}</TableCell>
                        <TableCell>
                          <Chip 
                            label={parseAmount(purchase.total_remained_amount) === 0 ? 'Paid' : parseAmount(purchase.total_paid) > 0 ? 'Partial' : 'Unpaid'} 
                            size="small" 
                            color={parseAmount(purchase.total_remained_amount) === 0 ? 'success' : parseAmount(purchase.total_paid) > 0 ? 'warning' : 'error'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ backgroundColor: 'primary.light', fontWeight: 'bold' }}>
                      <TableCell colSpan={3}><strong>TOTAL ({purchases.length} purchases)</strong></TableCell>
                      <TableCell><strong>{summary.total_purchases.amount.toFixed(2)}</strong></TableCell>
                      <TableCell><strong>{summary.total_paid.toFixed(2)}</strong></TableCell>
                      <TableCell><strong>{summary.outstanding_amount.toFixed(2)}</strong></TableCell>
                      <TableCell><strong>Outstanding</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>

            {/* Payment Tab */}
            <TabPanel value={tabValue} index={1}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Purchase</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Currency</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell>#{payment.id}</TableCell>
                        <TableCell>Purchase #{payment.purchase}</TableCell>
                        <TableCell>{parseAmount(payment.amount).toFixed(2)}</TableCell>
                        <TableCell>{payment.payment_date}</TableCell>
                        <TableCell>{payment.currency_details?.title || 'USD'}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ backgroundColor: 'success.light', fontWeight: 'bold' }}>
                      <TableCell colSpan={2}><strong>TOTAL ({payments.length} payments)</strong></TableCell>
                      <TableCell><strong>{summary.total_payments.amount.toFixed(2)}</strong></TableCell>
                      <TableCell colSpan={2}><strong>Total Paid</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>

            {/* Return Tab */}
            <TabPanel value={tabValue} index={2}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Purchase</TableCell>
                      <TableCell>Return Date</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Refund Amount</TableCell>
                      <TableCell>Items</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {returns.map((returnItem) => (
                      <TableRow key={returnItem.id}>
                        <TableCell>#{returnItem.id}</TableCell>
                        <TableCell>Purchase #{returnItem.purchase_details?.id || 'N/A'}</TableCell>
                        <TableCell>{returnItem.return_date}</TableCell>
                        <TableCell>{returnItem.reason || '-'}</TableCell>
                        <TableCell>{parseAmount(returnItem.total_refund_amount || (returnItem.return_items?.[0]?.refund_amount) || 0).toFixed(2)}</TableCell>
                        <TableCell>
                          <Chip label={returnItem.return_items?.length || 0} size="small" />
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ backgroundColor: 'info.light', fontWeight: 'bold' }}>
                      <TableCell colSpan={4}><strong>TOTAL ({returns.length} returns)</strong></TableCell>
                      <TableCell><strong>{summary.total_returns.amount.toFixed(2)}</strong></TableCell>
                      <TableCell><strong>{returns.reduce((sum, r) => sum + (r.return_items?.length || 0), 0)} items returned</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>
          </Paper>
        </Box>
      )}
    </Box>
  );
}
